import React from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  Mail,
  Shield,
  ShieldCheck
} from 'lucide-react';

const CihLogin = ({
  formData,
  handleChange,
  showPassword,
  setShowPassword,
  displayError,
  errorType,
  handleSubmit,
  loading,
  contextLoading,
  totpRequired,
  totpSetup,
  totpCode,
  setTotpCode,
  setError,
  resetTotpFlow
}) => (
  <>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');

      .cih-login-page,
      .cih-login-page * {
        box-sizing: border-box;
        font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
      }

      .cih-login-page {
        min-height: 100vh;
        display: grid;
        grid-template-columns: minmax(320px, 440px) 1fr;
        background: linear-gradient(135deg, #fdfcf5 0%, #eef7f6 46%, #f8fbfa 100%);
        color: #174954;
      }

      .cih-brand-panel {
        position: relative;
        min-height: 100vh;
        padding: 44px 42px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        overflow: hidden;
        background: #174954;
        color: #fdfcf5;
        border-right: 6px solid #fed774;
      }

      .cih-logo-row {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        gap: 14px;
        font-size: 20px;
        font-weight: 800;
      }

      .cih-logo-row img {
        width: 44px;
        height: 44px;
        object-fit: contain;
        border-radius: 8px;
        background: #fdfcf5;
        padding: 5px;
      }

      .cih-brand-content {
        position: relative;
        z-index: 1;
        max-width: 320px;
      }

      .cih-kicker {
        display: inline-flex;
        align-items: center;
        height: 32px;
        padding: 0 12px;
        border-radius: 8px;
        background: #fed774;
        color: #174954;
        font-size: 13px;
        font-weight: 800;
        letter-spacing: 0;
      }

      .cih-brand-content h1 {
        margin: 22px 0 14px;
        font-size: 54px;
        line-height: 0.96;
        letter-spacing: 0;
      }

      .cih-brand-content p {
        margin: 0;
        color: rgba(253, 252, 245, 0.78);
        font-size: 16px;
        line-height: 1.65;
      }

      .cih-status {
        position: relative;
        z-index: 1;
        display: inline-flex;
        align-items: center;
        gap: 10px;
        align-self: flex-start;
        padding: 12px 14px;
        border-radius: 8px;
        background: rgba(253, 252, 245, 0.1);
        color: #fdfcf5;
        font-size: 13px;
        font-weight: 700;
        border: 1px solid rgba(253, 252, 245, 0.16);
      }

      .cih-main {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 44px 28px;
      }

      .cih-shell {
        width: min(100%, 470px);
      }

      .cih-card {
        width: 100%;
        border-radius: 8px;
        border: 1px solid rgba(68, 134, 150, 0.2);
        background: rgba(255, 255, 255, 0.92);
        box-shadow: 0 24px 70px rgba(23, 73, 84, 0.16);
        padding: 34px;
      }

      .cih-card-head {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 24px;
      }

      .cih-mark {
        width: 54px;
        height: 54px;
        flex: 0 0 auto;
        border-radius: 8px;
        display: grid;
        place-items: center;
        color: #174954;
        background: linear-gradient(135deg, #fed774 0%, #fdac84 100%);
      }

      .cih-label {
        margin: 0 0 4px;
        color: #448696;
        font-size: 12px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0;
      }

      .cih-card h2 {
        margin: 0;
        color: #174954;
        font-size: 28px;
        font-weight: 800;
        line-height: 1.1;
      }

      .cih-error {
        display: flex;
        gap: 12px;
        align-items: flex-start;
        margin-bottom: 18px;
        padding: 13px 14px;
        border-radius: 8px;
        border: 1px solid rgba(220, 38, 38, 0.22);
        background: #fff4f4;
        color: #991b1b;
        font-size: 13px;
        font-weight: 700;
      }

      .cih-error.error-blocked,
      .cih-error.error-suspended,
      .cih-error.error-expired {
        border-color: rgba(217, 119, 6, 0.28);
        background: #fff7ed;
        color: #92400e;
      }

      .cih-field {
        display: block;
        margin-bottom: 14px;
      }

      .cih-field span {
        display: block;
        margin-bottom: 8px;
        color: #47646a;
        font-size: 12px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0;
      }

      .cih-input-wrap {
        position: relative;
      }

      .cih-input-icon {
        position: absolute;
        left: 15px;
        top: 50%;
        transform: translateY(-50%);
        color: #448696;
        pointer-events: none;
      }

      .cih-input {
        width: 100%;
        height: 52px;
        border: 1px solid #d8e5e7;
        border-radius: 8px;
        background: #fbfdfd;
        color: #174954;
        font-size: 16px;
        font-weight: 650;
        outline: none;
        padding: 0 46px;
        transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
      }

      .cih-input:focus {
        border-color: #448696;
        background: #ffffff;
        box-shadow: 0 0 0 4px rgba(68, 134, 150, 0.14);
      }

      .cih-eye {
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        width: 36px;
        height: 36px;
        display: grid;
        place-items: center;
        border: 0;
        border-radius: 8px;
        background: transparent;
        color: #448696;
        cursor: pointer;
      }

      .cih-totp-block {
        border: 1px solid rgba(68, 134, 150, 0.2);
        border-radius: 8px;
        background: #f8fbfa;
        padding: 18px;
        margin-bottom: 16px;
      }

      .cih-back {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        margin: 0 0 14px;
        border: 0;
        background: transparent;
        color: #448696;
        font-size: 13px;
        font-weight: 800;
        cursor: pointer;
        padding: 0;
      }

      .cih-totp-block h3 {
        margin: 0 0 8px;
        color: #174954;
        font-size: 21px;
        font-weight: 800;
      }

      .cih-totp-block p {
        margin: 0 0 14px;
        color: #5f7075;
        line-height: 1.5;
        font-size: 14px;
      }

      .cih-qr-row {
        display: grid;
        grid-template-columns: 160px 1fr;
        gap: 14px;
        align-items: center;
        margin-bottom: 14px;
      }

      .cih-qr {
        width: 160px;
        height: 160px;
        border-radius: 8px;
        border: 1px solid #d8e5e7;
        background: #ffffff;
        padding: 8px;
      }

      .cih-secret {
        display: block;
        overflow-wrap: anywhere;
        border-radius: 8px;
        border: 1px dashed #aac5ca;
        background: #ffffff;
        padding: 12px;
        color: #1f2937;
        font-size: 13px;
        font-weight: 800;
        letter-spacing: 0;
      }

      .cih-secret-label {
        margin-bottom: 7px;
        color: #6b7280;
        font-size: 11px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0;
      }

      .cih-submit {
        width: 100%;
        height: 54px;
        border: 0;
        border-radius: 8px;
        background: #448696;
        color: #ffffff;
        font-size: 15px;
        font-weight: 800;
        cursor: pointer;
        transition: transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
        box-shadow: 0 14px 24px rgba(68, 134, 150, 0.22);
      }

      .cih-submit:hover:not(:disabled) {
        background: #377483;
        transform: translateY(-1px);
      }

      .cih-submit:disabled {
        opacity: 0.65;
        cursor: not-allowed;
        box-shadow: none;
      }

      .cih-footnote {
        margin: 18px 0 0;
        color: #6b7d82;
        font-size: 12px;
        line-height: 1.5;
        text-align: center;
      }

      @media (max-width: 900px) {
        .cih-login-page {
          grid-template-columns: 1fr;
        }

        .cih-brand-panel {
          min-height: auto;
          padding: 30px 24px;
          gap: 32px;
          border-right: 0;
          border-bottom: 6px solid #fed774;
        }

        .cih-brand-content h1 {
          font-size: 36px;
        }

        .cih-main {
          min-height: auto;
          padding: 28px 18px 38px;
        }

        .cih-card {
          padding: 24px;
        }

        .cih-qr-row {
          grid-template-columns: 1fr;
        }

        .cih-qr {
          width: 150px;
          height: 150px;
        }
      }
    `}</style>

    <main className="cih-login-page">
      <section className="cih-brand-panel" aria-label="Horapilot CIH">
        <div className="cih-logo-row">
          <img src="/logo.png" alt="Horapilot" />
          <span>HoraPilot</span>
        </div>

        <div className="cih-brand-content">
          <span className="cih-kicker">CIH</span>
          <h1>Centre d'installation Horapilot</h1>
          <p>Acces reserve aux administrateurs Horapilot connectes au reseau prive Tailscale.</p>
        </div>

        <div className="cih-status">
          <ShieldCheck size={18} />
          <span>Acces prive securise</span>
        </div>
      </section>

      <section className="cih-main" aria-label="Connexion CIH">
        <div className="cih-shell">
          <div className="cih-card">
            <div className="cih-card-head">
              <div className="cih-mark">
                {totpRequired ? <Lock size={26} /> : <Shield size={26} />}
              </div>
              <div>
                <p className="cih-label">Administration</p>
                <h2>{totpRequired ? 'Double authentification' : 'Connexion CIH'}</h2>
              </div>
            </div>

            {displayError && (
              <div className={`cih-error error-${errorType || 'error'}`}>
                <AlertTriangle size={18} />
                <span>{displayError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {!totpRequired && (
                <>
                  <label className="cih-field">
                    <span>Adresse e-mail</span>
                    <div className="cih-input-wrap">
                      <Mail className="cih-input-icon" size={18} />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="cih-input"
                        placeholder="admin@horapilot.com"
                        autoComplete="email"
                        required
                      />
                    </div>
                  </label>

                  <label className="cih-field">
                    <span>Mot de passe</span>
                    <div className="cih-input-wrap">
                      <KeyRound className="cih-input-icon" size={18} />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="cih-input"
                        placeholder="Mot de passe"
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        className="cih-eye"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </label>
                </>
              )}

              {totpRequired && (
                <div className="cih-totp-block">
                  <button type="button" className="cih-back" onClick={resetTotpFlow}>
                    <ArrowLeft size={16} />
                    <span>Modifier les identifiants</span>
                  </button>

                  <h3>Google Authenticator</h3>
                  <p>Scanne le QR code avec Google Authenticator, puis saisis le code genere.</p>

                  {(totpSetup?.qrCodeDataUrl || totpSetup?.secret) && (
                    <div className="cih-qr-row">
                      {totpSetup?.qrCodeDataUrl && (
                        <img className="cih-qr" src={totpSetup.qrCodeDataUrl} alt="QR code Google Authenticator" />
                      )}
                      {totpSetup?.secret && (
                        <div>
                          <div className="cih-secret-label">Cle manuelle</div>
                          <code className="cih-secret">{totpSetup.secret}</code>
                        </div>
                      )}
                    </div>
                  )}

                  {!totpSetup?.secret && !totpSetup?.qrCodeDataUrl && (
                    <p>Saisis le code a 6 chiffres de ton application Google Authenticator.</p>
                  )}

                  <label className="cih-field">
                    <span>Code OTP</span>
                    <div className="cih-input-wrap">
                      <Lock className="cih-input-icon" size={18} />
                      <input
                        type="text"
                        name="totpCode"
                        value={totpCode}
                        onChange={(event) => { setTotpCode(event.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                        className="cih-input"
                        placeholder="000000"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                      />
                    </div>
                  </label>
                </div>
              )}

              <button type="submit" disabled={loading || contextLoading} className="cih-submit">
                {totpRequired ? 'Valider le code' : 'Continuer'}
              </button>
            </form>

            <p className="cih-footnote">CIH Horapilot. Cette interface est reservee aux operations d'administration.</p>
          </div>
        </div>
      </section>
    </main>
  </>
);

export default CihLogin;
